/**
 * WHERE THE REACTION BAR GOES (ITP-E-240-09, owner review 2026-10-09: "like WhatsApp, up the emoji icon"). Carbon's
 * `Menu.tsx` placement (`:121-161`) as the Chat panel's reaction bar uses it (`ChatPanel.tsx:2680-2701`: `alignment="left"`,
 * `bounds={scrollRef}`, `prefer="above"`), as a pure function of three rectangles in viewport pixels:
 *
 * - the bar's left edge on the React button's left edge, then held `EDGE` inside the thread's scroll box, so a button far
 *   to the right of a narrow panel pulls the bar back from the box's right edge;
 * - above the button, `GAP` off it, when that fits inside the box's top `EDGE`; else below it, `GAP` off it;
 * - then clamped inside the box, top and bottom.
 *
 * Bounded by the thread, not the window: in the Chat panel the thread starts under the panel header and ends above the
 * composer, so at the top of a thread there is no room above and the bar goes below rather than over the header. The CDK's
 * connected position strategy flips against the window, which is why the overlay is handed this answer instead.
 */

/** How far the bar is held inside the thread's box (carbon's `EDGE`). */
export const REACTION_PICKER_EDGE = 8;
/** How far the bar sits off its button (carbon's `GAP`). */
export const REACTION_PICKER_GAP = 4;

export interface PickerBox {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

export interface PickerSize {
  width: number;
  height: number;
}

export interface PickerPlacement {
  top: number;
  left: number;
  /** The side of the button it was put on, before the clamp. */
  side: 'above' | 'below';
}

export function reactionPickerPlacement(trigger: PickerBox, bar: PickerSize, bounds: PickerBox): PickerPlacement {
  const minX = bounds.left + REACTION_PICKER_EDGE;
  const maxX = bounds.right - REACTION_PICKER_EDGE;
  const minY = bounds.top + REACTION_PICKER_EDGE;
  const maxY = bounds.bottom - REACTION_PICKER_EDGE;

  // The far edge first and the near edge last, so a bar wider than its box starts at the near edge.
  const left = Math.max(minX, Math.min(trigger.left, maxX - bar.width));

  const above = trigger.top - REACTION_PICKER_GAP - bar.height;
  const side = above >= minY ? 'above' : 'below';
  const wanted = side === 'above' ? above : trigger.bottom + REACTION_PICKER_GAP;
  const top = Math.max(minY, Math.min(wanted, maxY - bar.height));

  return {top, left, side};
}
